import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
  type User,
} from "firebase/auth";
import { useEffect, useState } from "react";
import { auth } from "./firebase";

export const PHOTOSPICKER_SCOPE =
  "https://www.googleapis.com/auth/photospicker.mediaitems.readonly";

// Single-tenant allowlist. Sign-in succeeds for these accounts only;
// any other account is signed out immediately with an error.
// Firestore rules are already UID-scoped so a stranger couldn't see
// anyone else's data, but this stops them creating a profile + pinging
// our Auth quota.
const ALLOWED_EMAILS = new Set([
  "taofan18@gmail.com",
  "verajiecheng@gmail.com",
]);

export class UnauthorisedAccountError extends Error {
  constructor(email: string) {
    super(`This app is private to Vera Cheng's Family Day Care. ${email} is not on the access list.`);
    this.name = "UnauthorisedAccountError";
  }
}

export type SignInResult = {
  user: User;
  accessToken: string | null;
};

export async function signIn(): Promise<SignInResult> {
  const provider = new GoogleAuthProvider();
  provider.addScope(PHOTOSPICKER_SCOPE);
  const result = await signInWithPopup(auth, provider);
  const email = result.user.email?.toLowerCase() ?? "";
  if (!ALLOWED_EMAILS.has(email)) {
    await fbSignOut(auth);
    throw new UnauthorisedAccountError(email || "this account");
  }
  const credential = GoogleAuthProvider.credentialFromResult(result);
  const accessToken = credential?.accessToken ?? null;
  if (accessToken) writeCachedToken(accessToken);
  return { user: result.user, accessToken };
}

// Photos Picker access token cached in sessionStorage so subsequent calls
// (Generate Drafts, + Photos, Render PDFs) don't pop a Firebase OAuth
// popup. First sign-in of the session populates the cache; we re-popup
// only when the token has expired or the tab is closed.
//
// Threat model: the cached token is scoped photospicker.mediaitems.readonly,
// which is read-only AND only valid against picker sessions Vera explicitly
// creates. A leaked token can't read her Photos library at large. We use
// sessionStorage instead of localStorage to limit attacker window — token
// is gone the moment the tab closes. Trade-off: Vera signs in fresh on
// every new browser session, but that's a single popup per day.
const TOKEN_KEY = "_ls_picker_token";

type CachedToken = { token: string; expiresAt: number };

export function getCachedToken(): string | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedToken;
    if (parsed.expiresAt > Date.now()) return parsed.token;
  } catch { /* fall through */ }
  return null;
}

function writeCachedToken(token: string): void {
  // Conservative: assume 50 minutes of validity even though Google issues 1hr.
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify({
    token,
    expiresAt: Date.now() + 50 * 60 * 1000,
  } satisfies CachedToken));
}

function clearCachedToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

// Returns a Photos Picker access token. Uses the cached one if fresh,
// otherwise opens the Firebase sign-in popup to refresh.
export async function getAccessToken(): Promise<string> {
  const cached = getCachedToken();
  if (cached) return cached;
  const result = await signIn();
  if (!result.accessToken) throw new Error("No access token from sign-in");
  return result.accessToken;
}

export async function signOut(): Promise<void> {
  clearCachedToken();
  await fbSignOut(auth);
}

export function useAuth(): { user: User | null; loading: boolean } {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  return { user, loading };
}
